import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages } from '../data/mock';
import { ArrowRight, ArrowUpRight, Heart, Baby, Truck, ShieldCheck, Sparkles } from 'lucide-react';

const DISPLAY = { fontFamily: "'Quicksand', sans-serif", fontWeight: 700 };

const HomePage = () => {
  const featuredProducts = products.slice(0, 4);
  const bestSellers = products.filter(p => p.badge === 'Bestseller').slice(0, 4);

  useEffect(() => {
    document.title = 'Tiny Wonders Pick | Sweet Baby Essentials & Newborn Layette';
  }, []);

  const ageGroups = [
    { title: 'Footies & One-Pieces', sub: 'Cozy from head to toe', slug: 'footies', image: siteImages.heroSecondary },
    { title: 'Sleeper Gowns', sub: 'Sweet dreams, easy changes', slug: 'gowns', image: siteImages.heroLifestyle },
    { title: 'Gift Sets', sub: 'Ready-to-give keepsakes', slug: 'gifts', image: categories.find(c => c.id === 'gifts')?.image },
  ];

  return (
    <div className="min-h-screen bg-white text-[#3a2a32]" data-testid="home-page">
      <Header />
      <CartDrawer />

      <main className="pt-[104px]">
        {/* ================= HERO ================= */}
        <section className="relative bg-[#fdf4f7] overflow-hidden">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-white border border-athletic-200 rounded-full mb-8">
                  <Heart className="w-3.5 h-3.5 text-athletic-500" />
                  <span className="text-athletic-700 text-[11px] font-semibold tracking-[0.18em] uppercase">Welcome to the World</span>
                </div>

                <h1 className="text-5xl sm:text-6xl lg:text-7xl leading-[1.04] mb-8 text-[#3a2a32]"
                    style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 700, letterSpacing: '-0.02em' }}>
                  The softest start<br />
                  for your<br />
                  <span className="text-athletic-500">little wonder.</span>
                </h1>

                <p className="text-base text-[#3a2a32]/70 leading-relaxed mb-10 max-w-md">
                  Gentle cotton footies, dreamy sleeper gowns, snuggly blankets and keepsake gift sets - thoughtfully made for newborns and the tiniest moments.
                </p>

                <div className="flex flex-wrap gap-4 mb-12">
                  <Link
                    to="/shop"
                    data-testid="hero-shop-btn"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-semibold tracking-wide rounded-full transition-all"
                  >
                    Shop Newborn Essentials
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/why-choose-us"
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-transparent border border-[#3a2a32]/20 hover:border-[#3a2a32] text-[#3a2a32] font-semibold tracking-wide rounded-full transition-all"
                  >
                    Our Story
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="flex gap-10 pt-8 border-t border-[#3a2a32]/10">
                  {[
                    { value: '100%', label: 'Soft Cotton' },
                    { value: '$65', label: 'Free Shipping' },
                    { value: '30d', label: 'Easy Returns' },
                  ].map((stat, i) => (
                    <div key={i}>
                      <p className="text-3xl text-athletic-500" style={DISPLAY}>{stat.value}</p>
                      <p className="text-[10px] text-[#3a2a32]/50 uppercase tracking-[0.18em] mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="aspect-[4/5] rounded-[2rem] overflow-hidden bg-white">
                  <img
                    src={siteImages.heroMain}
                    alt="Tiny Wonders Pick baby essentials"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white shadow-lg rounded-2xl p-5 border border-gray-100 max-w-[230px]">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-athletic-500" />
                    <span className="text-[10px] uppercase tracking-[0.18em] text-[#3a2a32]/60 font-medium">Parent Favorite</span>
                  </div>
                  <p className="text-sm text-[#3a2a32] leading-snug">Buttery-soft footies and matching hats for those first sweet snapshots.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= TRUST STRIP ================= */}
        <section className="bg-white border-y border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { icon: Heart, text: 'Softest Cotton' },
                { icon: Baby, text: 'Made for Newborns' },
                { icon: Truck, text: 'Free US Shipping $65+' },
                { icon: ShieldCheck, text: '30-Day Returns' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 justify-center md:justify-start">
                  <item.icon className="w-5 h-5 text-athletic-500" strokeWidth={1.5} />
                  <span className="text-[#3a2a32]/70 text-sm tracking-wide">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= CATEGORIES ================= */}
        <section className="py-24 bg-white" data-testid="home-categories">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-athletic-500 text-[11px] font-semibold uppercase tracking-[0.25em]">Shop by Category</span>
              <h2 className="text-4xl lg:text-5xl mt-3 text-[#3a2a32]" style={DISPLAY}>
                Sweet little <span className="text-athletic-500">essentials</span>
              </h2>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/shop?category=${category.slug}`}
                  data-testid={`home-cat-${category.slug}`}
                  className="group relative aspect-[4/5] rounded-[1.75rem] overflow-hidden bg-[#fdf4f7]"
                >
                  <img
                    src={category.image}
                    alt={category.name}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/0 to-transparent" />
                  <div className="absolute inset-0 flex flex-col justify-end p-6">
                    <h3 className="text-2xl text-white" style={DISPLAY}>
                      {category.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-2 text-white/80 group-hover:text-athletic-200 transition-colors">
                      <span className="text-xs uppercase tracking-wider">Shop now</span>
                      <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ================= BESTSELLERS ================= */}
        <section className="py-24 bg-[#fdf4f7]" data-testid="home-bestsellers">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-12 gap-6">
              <div>
                <span className="text-athletic-500 text-[11px] font-semibold uppercase tracking-[0.25em]">Most Loved</span>
                <h2 className="text-4xl lg:text-5xl mt-3 text-[#3a2a32]" style={DISPLAY}>
                  Parent Favorites
                </h2>
              </div>
              <Link
                to="/shop"
                className="group inline-flex items-center gap-2 text-[#3a2a32]/60 hover:text-athletic-500 font-semibold transition-colors text-sm tracking-wide"
              >
                Shop all
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {(bestSellers.length ? bestSellers : featuredProducts).map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  data-testid={`home-product-${product.slug}`}
                  className="group block"
                >
                  <div className="aspect-square rounded-2xl bg-white overflow-hidden mb-4 relative border border-gray-100">
                    <img
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {product.badge && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-white/95 text-[#3a2a32] text-[10px] font-semibold tracking-[0.12em] uppercase rounded-full">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-athletic-500 uppercase tracking-[0.18em] font-semibold mb-1">
                    {categories.find(c => c.id === product.categoryId)?.name}
                  </p>
                  <h3 className="text-base text-[#3a2a32] mb-2 group-hover:text-athletic-500 transition-colors line-clamp-2 min-h-[44px]">
                    {product.name}
                  </h3>
                  <p className="text-base text-[#3a2a32]" style={DISPLAY}>
                    ${product.price.toFixed(2)}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ================= SOFTNESS STORY ================= */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="aspect-[4/5] rounded-[2rem] overflow-hidden bg-[#fdf4f7]">
                  <img
                    src={siteImages.featuredBackground}
                    alt="Soft baby fabrics"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="absolute -bottom-6 -right-6 bg-athletic-500 text-white p-6 rounded-2xl max-w-[210px]">
                  <p className="text-4xl" style={DISPLAY}>100%</p>
                  <p className="text-xs uppercase tracking-[0.15em] mt-1 opacity-90">Made for cuddles</p>
                </div>
              </div>

              <div className="order-1 lg:order-2">
                <span className="text-athletic-500 text-[11px] font-semibold uppercase tracking-[0.25em]">There is soft, and then there is this</span>
                <h2 className="text-4xl lg:text-5xl mt-3 mb-8 text-[#3a2a32]" style={DISPLAY}>
                  Gentle on skin,<br />
                  <span className="text-athletic-500">built to last.</span>
                </h2>
                <p className="text-[#3a2a32]/70 leading-relaxed mb-10">
                  Every Tiny Wonders Pick piece is chosen for the softest feel against delicate newborn skin. Think breathable cotton, tagless necklines, easy zips and snaps - all made to survive countless washes and still feel cloud-soft.
                </p>

                <div className="grid grid-cols-2 gap-6">
                  {[
                    { title: 'Comfort', desc: 'Soft cotton and tagless seams all day.' },
                    { title: 'Easy Changes', desc: 'Smart zips, snaps and open hems.' },
                    { title: 'Durability', desc: 'Built to stay soft wash after wash.' },
                    { title: 'Sweet Style', desc: 'Charming prints worth treasuring.' },
                  ].map((item, i) => (
                    <div key={i} className="border-t border-[#3a2a32]/10 pt-4">
                      <h3 className="text-xl text-[#3a2a32] mb-1" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}>
                        {item.title}
                      </h3>
                      <p className="text-sm text-[#3a2a32]/60 leading-relaxed">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= SHOP HIGHLIGHTS ================= */}
        <section className="py-24 bg-[#fdf4f7]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-athletic-500 text-[11px] font-semibold uppercase tracking-[0.25em]">Mom & Dad Favorites</span>
              <h2 className="text-4xl lg:text-5xl mt-3 text-[#3a2a32]" style={DISPLAY}>
                Where to <span className="text-athletic-500">begin</span>
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {ageGroups.map((g, i) => (
                <Link
                  key={i}
                  to={`/shop?category=${g.slug}`}
                  className="group relative rounded-[1.75rem] overflow-hidden bg-white border border-gray-100"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={g.image} alt={g.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl text-[#3a2a32]" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}>{g.title}</h3>
                    <p className="text-sm text-[#3a2a32]/60 mt-1 mb-3">{g.sub}</p>
                    <span className="inline-flex items-center gap-2 text-athletic-500 text-sm font-semibold">
                      Shop now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ================= STEPS ================= */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-16">
              <span className="text-athletic-500 text-[11px] font-semibold uppercase tracking-[0.25em]">How It Works</span>
              <h2 className="text-4xl lg:text-5xl mt-3 text-[#3a2a32]" style={DISPLAY}>
                Sweet & <span className="text-athletic-500">simple</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
              {[
                { num: '01', title: 'Browse', desc: 'Explore footies, gowns, blankets and gift sets.' },
                { num: '02', title: 'Pick Favorites', desc: 'Choose the prints and essentials baby will love.' },
                { num: '03', title: 'Checkout', desc: 'Secure, encrypted payment in just a few taps.' },
                { num: '04', title: 'Snuggle Up', desc: 'Open the box and wrap your little one in softness.' },
              ].map((step, i) => (
                <div key={i} className="text-center">
                  <p className="text-5xl text-athletic-500 mb-4" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}>
                    {step.num}
                  </p>
                  <h3 className="text-2xl text-[#3a2a32] mb-3" style={{ fontFamily: "'Quicksand', sans-serif", fontWeight: 600 }}>
                    {step.title}
                  </h3>
                  <p className="text-sm text-[#3a2a32]/60 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= CTA ================= */}
        <section className="relative py-28 bg-[#fdf4f7]">
          <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-5xl lg:text-6xl text-[#3a2a32] mb-6 leading-tight" style={DISPLAY}>
              Ready to <span className="text-athletic-500">cuddle?</span>
            </h2>
            <p className="text-base text-[#3a2a32]/70 mb-10 max-w-lg mx-auto leading-relaxed">
              Find the sweetest essentials for your little wonder - soft, snuggly and ready to ship.
            </p>
            <Link
              to="/shop"
              data-testid="cta-shop-btn"
              className="inline-flex items-center gap-3 px-10 py-4 bg-athletic-500 hover:bg-athletic-600 text-white font-semibold tracking-wide rounded-full transition-all"
            >
              Shop Baby Essentials
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
